import { useCallback, useEffect, useRef, useState } from 'react';
import RuleExceptionUploadModal from '../common/RuleExceptionUploadModal/RuleExceptionUploadModal';
import './BulkUpload.css';
import { uploadRuleExceptionsAllEndPoint } from './utils';
import axios from 'axios';
import * as FileSaver from 'file-saver';
import {
    ColumnDirective,
    ColumnsDirective,
    Filter,
    GridComponent,
    Inject,
    Page,
    Sort,
} from '@syncfusion/ej2-react-grids';

import DispositionUploadTemplate from '../../assets/templates/disposition-upload-template.xlsx';
import moment from 'moment';

export default function BulkUploadRuleException({
    uploadModalClose,
    show = false,
}: any) {
    const [summaryList, setSummaryList] = useState<any>([]);
    const [fileName, setFileName] = useState('');
    const [file, setFile] = useState<any>();
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const gridRef = useRef<GridComponent | null>(null);

    useEffect(() => {
        vendorUploadSummary();
    }, []);

    const syncHeaderScrollGap = useCallback(() => {
        const gridEl = gridRef.current?.element as HTMLElement | undefined;
        if (!gridEl) {
            return;
        }

        const content = gridEl.querySelector(
            '.e-content'
        ) as HTMLElement | null;
        const header = gridEl.querySelector(
            '.e-gridheader'
        ) as HTMLElement | null;
        if (!content || !header) {
            return;
        }

        const scrollbarWidth = Math.max(
            0,
            content.offsetWidth - content.clientWidth
        );
        header.style.setProperty(
            'padding-right',
            `${scrollbarWidth}px`,
            'important'
        );
    }, []);

    const onGridCreated = useCallback(() => {
        requestAnimationFrame(syncHeaderScrollGap);
    }, [syncHeaderScrollGap]);

    const onGridDataBound = useCallback(() => {
        requestAnimationFrame(syncHeaderScrollGap);
    }, [syncHeaderScrollGap]);

    useEffect(() => {
        const onResize = () => syncHeaderScrollGap();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [syncHeaderScrollGap]);

    useEffect(() => {
        requestAnimationFrame(syncHeaderScrollGap);
    }, [summaryList, syncHeaderScrollGap]);

    const downloadErrorTemplate = useCallback(async (row: any) => {
        try {
            const uploadId = row?.uploadId;
            if (!uploadId) {
                return;
            }

            const fetchAPI = uploadRuleExceptionsAllEndPoint(
                'downloadBulkUploadRuleException',
                uploadId
            );
            const response: any = await axios.get(fetchAPI, {
                responseType: 'blob',
            });

            if (response?.status === 200 && response.data) {
                const blob = new Blob([response?.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });

                const disposition =
                    response.headers?.['content-disposition'] || '';
                const fileNameMatch = disposition.match(
                    /filename\*?=(?:UTF-8''|")?([^;"\n]+)/i
                );
                const serverFileName = fileNameMatch?.[1]
                    ? decodeURIComponent(fileNameMatch[1].replace(/"/g, ''))
                    : '';

                const baseName = row?.fileName
                    ? String(row.fileName).replace(/\.[^.]+$/, '')
                    : `upload-${uploadId}`;
                const downloadName =
                    serverFileName || `${baseName}-failures.xlsx`;

                FileSaver.saveAs(blob, downloadName);
            }
        } catch (err) {}
    }, []);

    const customizeCell = useCallback(
        (args: any) => {
            if (!args?.column?.field || !args?.cell) {
                return;
            }

            if (args.column.field === 'status') {
                const status = args.data?.[args.column.field] ?? '';
                const normalized = String(status).trim().toLowerCase();
                const isComplete =
                    normalized === 'complete' || normalized === 'completed';
                const statusClass = isComplete
                    ? 'status-active-field'
                    : 'status-inProgress-field';
                args.cell.innerHTML = `<span class="${statusClass}">${status}</span>`;
                return;
            }

            if (args.column.field === 'failureCount') {
                const count = Number(args.data?.failureCount ?? 0);
                const className = count === 0 ? 'success' : 'failure';
                args.cell.innerHTML = `<span class="${className}">${count}</span>`;
                args.cell.style.textAlign = 'center';
                args.cell.style.cursor = count > 0 ? 'pointer' : 'default';
                args.cell.onclick =
                    count > 0
                        ? () => {
                              downloadErrorTemplate(args.data);
                          }
                        : null;
            }
        },
        [downloadErrorTemplate]
    );

    const clearSelectedFile = useCallback(() => {
        setFileName('');
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const onChange = useCallback((e) => {
        const selectedFile = e.target.files?.[0];

        if (selectedFile) {
            setFileName(selectedFile.name);
            setFile(selectedFile);
        }
    }, []);

    const handleUpload = useCallback(async () => {
        try {
            let formData = new FormData();
            formData.append('file', file);
            setLoading(true);

            const fetchAPI = uploadRuleExceptionsAllEndPoint(
                'createBulkUploadRuleExceptions'
            );
            const response = await axios
                .post(fetchAPI, formData, {
                    headers: { 'content-type': 'multipart/form-data' },
                })
                .catch((err) => err);
            if (response.status === 200) {
                setLoading(false);
                clearSelectedFile();
                await vendorUploadSummary();
            }
        } catch (err) {
            setLoading(false);
        }
    }, [file, clearSelectedFile]);

    const vendorUploadSummary = useCallback(async () => {
        try {
            const fetchAPI = uploadRuleExceptionsAllEndPoint(
                'getAllbulkUploadRuleExceptions'
            );
            const response = await axios.get(fetchAPI).catch((err) => err);
            setSummaryList(response?.data || []);
        } catch (err) {}
    }, []);

    return (
        <RuleExceptionUploadModal
            show={show}
            handleClose={() => uploadModalClose?.()}
        >
            <div className="bulk-upload-rule-exception">
                <div className="bulk-upload-header">
                    <h2>Bulk Upload Rule Exceptions</h2>
                </div>

                <div className="bulk-upload-section">
                    <div className="bulk-upload-field">
                        <label
                            className="bulk-upload-label"
                            htmlFor="bulk-upload-rule-exception-file"
                        >
                            File Upload
                            <span className="bulk-upload-required">*</span>
                        </label>

                        <div className="bulk-upload-picker">
                            <input
                                id="bulk-upload-rule-exception-file"
                                ref={fileInputRef}
                                type="file"
                                className="bulk-upload-input"
                                accept=".xlsx,.xls,.csv"
                                onChange={onChange}
                            />
                            <label
                                className="bulk-upload-choose"
                                htmlFor="bulk-upload-rule-exception-file"
                            >
                                Choose File
                            </label>
                            <span
                                className={`bulk-upload-filename${
                                    fileName ? ' has-file' : ''
                                }`}
                                title={fileName || undefined}
                            >
                                {fileName || 'No file chosen'}
                            </span>
                            {fileName ? (
                                <button
                                    type="button"
                                    className="bulk-upload-clear"
                                    onClick={clearSelectedFile}
                                    aria-label="Clear selected file"
                                >
                                    ×
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <div className="bulk-upload-actions">
                        <button
                            type="button"
                            className="bulk-upload-submit"
                            disabled={!fileName || loading}
                            onClick={handleUpload}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm"></span>
                                    Uploading...
                                </>
                            ) : (
                                'Upload'
                            )}
                        </button>

                        <a
                            href={DispositionUploadTemplate}
                            download={`Blank_Disposition_Upload_Template_${moment().format(
                                'YYYYMMDD_HHmmss'
                            )}.xlsx`}
                            className="bulk-upload-template"
                        >
                            <svg
                                className="bulk-upload-template-icon"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M8.5 1.5a.5.5 0 0 0-1 0V9.3L5.35 7.15a.5.5 0 1 0-.7.7l3 3a.5.5 0 0 0 .7 0l3-3a.5.5 0 0 0-.7-.7L8.5 9.3V1.5z" />
                                <path d="M2.5 12.5A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5V11a.5.5 0 0 0-1 0v1.5a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V11a.5.5 0 0 0-1 0v1.5z" />
                            </svg>
                            Blank Disposition Upload Template
                        </a>
                    </div>
                </div>

                <div className="bulk-upload-grid" id="venderPriceListID">
                    <GridComponent
                        ref={(g) => {
                            gridRef.current = g;
                        }}
                        dataSource={summaryList}
                        queryCellInfo={customizeCell}
                        created={onGridCreated}
                        dataBound={onGridDataBound}
                        gridLines="Both"
                        allowFiltering={false}
                        rowHeight={32}
                        height="200"
                        allowPaging={true}
                        pageSettings={{ pageSize: 10 }}
                        allowExcelExport={true}
                        allowSorting={true}
                        allowReordering={false}
                        allowResizing={false}
                        width="100%"
                    >
                        <ColumnsDirective>
                            <ColumnDirective
                                field="fileName"
                                headerText="FILE NAME"
                                textAlign="Left"
                                width="140"
                                minWidth="120"
                            />
                            <ColumnDirective
                                field="userName"
                                headerText="USER NAME"
                                width="120"
                                minWidth="100"
                                textAlign="Left"
                            />
                            <ColumnDirective
                                field="totalRecords"
                                headerText="TOTAL RECORDS"
                                textAlign="Center"
                                width="140"
                                minWidth="110"
                            />
                            <ColumnDirective
                                field="successCount"
                                headerText="SUCCESS COUNT"
                                width="140"
                                minWidth="110"
                                textAlign="Center"
                            />
                            <ColumnDirective
                                field="failureCount"
                                headerText="FAILURE COUNT"
                                textAlign="Center"
                                width="140"
                                minWidth="110"
                            />
                            <ColumnDirective
                                field="status"
                                headerText="STATUS"
                                textAlign="Center"
                                width="110"
                                minWidth="100"
                            />
                        </ColumnsDirective>
                        <Inject services={[Page, Sort, Filter]} />
                    </GridComponent>
                </div>
            </div>
        </RuleExceptionUploadModal>
    );
}
